import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useTranslation } from '../../hooks/useTranslation';

// Mismos valores que los enums reales del backend (models.py) — cualquier
// cambio ahí debe reflejarse aquí a mano, no hay generación automática.
const STATUSES = [
  'NEW', 'READY_TO_CONTACT', 'CONTACTED_WHATSAPP', 'CONTACTED_EMAIL', 'CONTACTED_CALL',
  'FOLLOW_UP', 'DEMO_PENDING', 'DEMO_SCHEDULED', 'EVALUATING_PROPOSAL',
  'CUSTOMER', 'NOT_INTERESTED', 'DISCARDED', 'ERROR',
];
const PRIORITIES = ['high', 'medium', 'low', 'discard', 'unknown'];
const TEMPERATURES = ['HOT', 'WARM', 'COLD'];
// Exportado para reutilizar en AddLeadModal.jsx — una única fuente de
// verdad en el frontend, aunque ambas sigan dependiendo de mantenerse a
// mano en sincronía con el enum real de models.py.
export const SECTORS = [
  'personal_trainer', 'small_gym', 'functional_training', 'boutique_gym', 'cross_training',
  'pilates', 'yoga', 'physiotherapy', 'osteopathy', 'nutritionist', 'psychologist',
  'massage_therapist', 'manual_therapist', 'wellness_center', 'chiropractor', 'unknown',
];

const DEBOUNCE_MS = 350;

const DEFAULT_STATE = {
  sector: 'all', city: '', priority: 'all', status: 'all',
  temperature: 'all', minScore: '', search: '',
};

// Mapea el objeto de filtros con forma de query param de la API (el mismo
// que ya construye onFiltersChange más abajo) a la forma que usa el estado
// interno de este componente — inverso exacto de ese mapeo. Usado solo
// para sembrar el estado inicial desde la URL (LeadScannerPage.js), nunca
// para resincronizar en cada render.
function mapInitialFilters(initialFilters) {
  if (!initialFilters) return DEFAULT_STATE;
  return {
    sector: initialFilters.sector ?? 'all',
    city: initialFilters.city ?? '',
    priority: initialFilters.priority ?? 'all',
    status: initialFilters.status ?? 'all',
    temperature: initialFilters.temperature ?? 'all',
    minScore: initialFilters.min_score !== undefined ? String(initialFilters.min_score) : '',
    search: initialFilters.search ?? '',
  };
}

// onFiltersChange recibe el objeto ya mapeado a los query params reales de
// GET /leads (Commits 13/13.1/13.2/13.3) — sector/priority/status/
// temperature en 'all' se traducen a undefined (sin filtro), no al string
// literal "all". Un único debounce de 350ms para todos los campos —
// incluidos los selects, por simplicidad; la diferencia de UX entre
// "select dispara al instante" y "select dispara en 350ms" es
// imperceptible, y separar los dos casos no aporta nada aquí.
export function LeadFilters({ initialFilters, onFiltersChange }) {
  const { t } = useTranslation();
  // Lazy initializer — se evalúa una sola vez al montar, nunca se
  // resincroniza si initialFilters cambia en renders posteriores (evitaría
  // pisar lo que Marta esté escribiendo). El padre es quien posee el
  // estado persistente (URL); este componente solo necesita el valor de
  // arranque para no perderlo al volver de la ficha de un lead.
  const [state, setState] = useState(() => mapInitialFilters(initialFilters));

  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange({
        sector: state.sector === 'all' ? undefined : state.sector,
        city: state.city.trim() || undefined,
        priority: state.priority === 'all' ? undefined : state.priority,
        status: state.status === 'all' ? undefined : state.status,
        temperature: state.temperature === 'all' ? undefined : state.temperature,
        min_score: state.minScore !== '' ? Number(state.minScore) : undefined,
        search: state.search.trim() || undefined,
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const set = (field) => (value) => setState((prev) => ({ ...prev, [field]: value }));
  const handleClear = () => setState(DEFAULT_STATE);
  const hasActiveFilters = JSON.stringify(state) !== JSON.stringify(DEFAULT_STATE);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <div className="lg:col-span-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={state.search}
              onChange={(e) => set('search')(e.target.value)}
              placeholder={t('aiLeadScanner.filters.searchPlaceholder')}
              className="pl-8"
              data-testid="lead-filter-search"
            />
          </div>
        </div>

        <Select value={state.sector} onValueChange={set('sector')}>
          <SelectTrigger data-testid="lead-filter-sector">
            <SelectValue placeholder={t('aiLeadScanner.filters.sector')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('aiLeadScanner.filters.allSectors')}</SelectItem>
            {SECTORS.map((s) => (
              <SelectItem key={s} value={s}>{t(`aiLeadScanner.sector.${s}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={state.city}
          onChange={(e) => set('city')(e.target.value)}
          placeholder={t('aiLeadScanner.filters.city')}
          data-testid="lead-filter-city"
        />

        <Select value={state.priority} onValueChange={set('priority')}>
          <SelectTrigger data-testid="lead-filter-priority">
            <SelectValue placeholder={t('aiLeadScanner.filters.priority')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('aiLeadScanner.filters.allPriorities')}</SelectItem>
            {PRIORITIES.map((p) => (
              <SelectItem key={p} value={p}>{t(`aiLeadScanner.priority.${p}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={state.status} onValueChange={set('status')}>
          <SelectTrigger data-testid="lead-filter-status">
            <SelectValue placeholder={t('aiLeadScanner.filters.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('aiLeadScanner.filters.allStatuses')}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{t(`aiLeadScanner.status.${s}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={state.temperature} onValueChange={set('temperature')}>
          <SelectTrigger data-testid="lead-filter-temperature">
            <SelectValue placeholder={t('aiLeadScanner.filters.temperature')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('aiLeadScanner.filters.allTemperatures')}</SelectItem>
            {TEMPERATURES.map((tmp) => (
              <SelectItem key={tmp} value={tmp}>{t(`aiLeadScanner.temperature.${tmp}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="number"
          min={0}
          max={100}
          value={state.minScore}
          onChange={(e) => set('minScore')(e.target.value)}
          placeholder={t('aiLeadScanner.filters.minScore')}
          data-testid="lead-filter-min-score"
        />
      </div>

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={handleClear} className="mt-2 text-zinc-400">
          <X className="w-3.5 h-3.5 mr-1" />
          {t('aiLeadScanner.filters.clear')}
        </Button>
      )}
    </div>
  );
}
